import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, Save, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Admin — set/override the "Next Members Night" that shows on the landing
 * hero countdown. Clearing the override reverts to auto (next Friday 22:00 STHLM).
 */
export default function AdminEvent() {
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [override, setOverride] = useState(false);
    const [date, setDate] = useState("");   // yyyy-mm-dd
    const [time, setTime] = useState("22:00"); // HH:mm
    const [title, setTitle] = useState("Members Night");
    const [subtitle, setSubtitle] = useState("John Scotts · Kungsträdgården");
    const [publicIso, setPublicIso] = useState(null);

    useEffect(() => {
        if (!localStorage.getItem("sc_admin_token")) {
            nav("/admin/login");
            return;
        }
        (async () => {
            try {
                const [{ data: mine }, { data: pub }] = await Promise.all([
                    api.get("/admin/next-event"),
                    api.get("/public/next-event"),
                ]);
                setOverride(!!mine.override);
                if (mine.iso) {
                    const d = new Date(mine.iso);
                    // convert to local sthlm parts for the inputs — use ISO string parts
                    const iso = mine.iso;
                    // extract yyyy-mm-dd and HH:mm from the ISO (assumes STHLM offset preserved)
                    setDate(iso.slice(0, 10));
                    setTime(iso.slice(11, 16));
                    setTitle(mine.title || "Members Night");
                    setSubtitle(mine.subtitle || "");
                } else {
                    // preload with auto value so the form shows what the site is currently displaying
                    const iso = pub.iso;
                    setDate(iso.slice(0, 10));
                    setTime(iso.slice(11, 16));
                    setTitle(pub.title || "Members Night");
                    setSubtitle(pub.subtitle || "");
                }
                setPublicIso(pub.iso);
            } catch (e) {
                if (e?.response?.status === 401) nav("/admin/login");
                toast.error("Could not load event settings");
            } finally {
                setLoading(false);
            }
        })();
    }, [nav]);

    const save = async () => {
        if (!date || !time) {
            toast.error("Please pick a date and time");
            return;
        }
        setSaving(true);
        try {
            // send date + time separately so backend interprets in Europe/Stockholm
            await api.put("/admin/next-event", { date, time, title, subtitle });
            toast.success("Event saved — landing hero updated");
            setOverride(true);
            const pub = await api.get("/public/next-event");
            setPublicIso(pub.data.iso);
        } catch {
            toast.error("Could not save");
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        setSaving(true);
        try {
            await api.put("/admin/next-event", { iso: null, date: null });
            toast.success("Reverted to auto (next Friday 22:00)");
            setOverride(false);
            const pub = await api.get("/public/next-event");
            setPublicIso(pub.data.iso);
            setDate(pub.data.iso.slice(0, 10));
            setTime(pub.data.iso.slice(11, 16));
            setTitle(pub.data.title);
            setSubtitle(pub.data.subtitle);
        } catch {
            toast.error("Could not clear");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-[100dvh] pb-24" data-testid="admin-event-page">
            {/* Header */}
            <header className="border-b border-emerald-line/60 bg-bg-secondary/60 backdrop-blur-md">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-secondary hover:text-gold"
                        data-testid="back-to-dashboard"
                    >
                        <ArrowLeft size={14} /> Dashboard
                    </Link>
                    <div className="flex items-center gap-2 text-gold text-[11px] tracking-[0.24em] uppercase">
                        <CalendarClock size={14} /> Event Override
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 lg:px-12 pt-16">
                <div className="label-eyebrow flex items-center gap-3">
                    <span className="w-8 h-px bg-gold" />
                    Landing Countdown
                </div>
                <h1 className="font-serif font-medium text-4xl md:text-5xl mt-4 leading-tight">
                    Next <span className="italic text-gold">Members Night</span>
                </h1>
                <p className="text-ink-secondary mt-4 max-w-xl leading-relaxed">
                    Set the exact date and time the landing hero counts down to. Leave
                    this untouched and the site auto-picks the next Friday 22:00 in Stockholm.
                </p>

                {loading ? (
                    <div className="mt-16 text-ink-secondary">Loading…</div>
                ) : (
                    <>
                        {/* Currently showing */}
                        <div className="mt-10 glass-card rounded-sm p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="current-showing">
                            <div>
                                <div className="label-eyebrow">Currently on landing</div>
                                <div className="font-serif text-2xl mt-1 text-ink-primary">
                                    {publicIso
                                        ? new Date(publicIso).toLocaleString("en-GB", {
                                            weekday: "long", day: "2-digit", month: "long",
                                            hour: "2-digit", minute: "2-digit",
                                            timeZone: "Europe/Stockholm",
                                        })
                                        : "—"}
                                </div>
                            </div>
                            <span className={`inline-flex items-center px-3 py-1.5 text-[10px] tracking-[0.24em] uppercase border rounded-sm ${override
                                ? "border-gold/50 text-gold bg-gold/10"
                                : "border-emerald-line text-ink-secondary bg-bg-secondary"}`}
                                data-testid="override-state">
                                {override ? "Manual override" : "Auto · Friday 22:00"}
                            </span>
                        </div>

                        {/* Form */}
                        <div className="mt-10 space-y-8">
                            <div className="grid sm:grid-cols-2 gap-6">
                                <label className="block">
                                    <span className="label-eyebrow">Date</span>
                                    <input
                                        type="date"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        className="input-line mt-3"
                                        data-testid="event-date-input"
                                    />
                                </label>
                                <label className="block">
                                    <span className="label-eyebrow">Time (STHLM)</span>
                                    <input
                                        type="time"
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                        className="input-line mt-3"
                                        data-testid="event-time-input"
                                    />
                                </label>
                            </div>
                            <label className="block">
                                <span className="label-eyebrow">Title</span>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Members Night"
                                    className="input-line mt-3"
                                    data-testid="event-title-input"
                                    maxLength={80}
                                />
                            </label>
                            <label className="block">
                                <span className="label-eyebrow">Subtitle</span>
                                <input
                                    type="text"
                                    value={subtitle}
                                    onChange={(e) => setSubtitle(e.target.value)}
                                    placeholder="John Scotts · Kungsträdgården"
                                    className="input-line mt-3"
                                    data-testid="event-subtitle-input"
                                    maxLength={120}
                                />
                            </label>
                        </div>

                        {/* Actions */}
                        <div className="mt-12 flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="save-event"
                                className="btn-gold inline-flex items-center justify-center gap-3 px-8 py-3.5 rounded-full font-medium tracking-wide disabled:opacity-60"
                            >
                                <Save size={16} />
                                <span className="text-sm tracking-[0.18em] uppercase">
                                    {saving ? "Saving…" : "Save & Update Landing"}
                                </span>
                            </button>
                            {override && (
                                <button
                                    onClick={clear}
                                    disabled={saving}
                                    data-testid="clear-event"
                                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-ink-secondary/40 text-ink-secondary rounded-full hover:border-gold hover:text-gold text-[11px] tracking-[0.22em] uppercase transition-all disabled:opacity-60"
                                >
                                    <RotateCcw size={14} />
                                    Revert to Auto
                                </button>
                            )}
                        </div>

                        <div className="mt-12 text-ink-muted text-xs leading-relaxed max-w-xl">
                            Note: the countdown on the landing page reads from Stockholm
                            time. Changes take effect immediately — no redeploy needed.
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
